import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import 'rxjs/add/operator/toPromise';

import {
  VisualItem,
  VisualItemsApiName,
  AcceptedCash,
  Order,
  Product,
  Money,
  VuState,
  DtoOrder,
  PrintTask,
  DtoVuConfiguration,
  TicketUse,
  CreditCard,
  OrderType,
  Ticket
} from '../../../lib/lib';
import { VuHttpBaseService } from './vu-http-base.service';
import {
  API_PRINT,
  API_PRINT_TEMPLATE,
  API_SAVE_ORDER,
  API_CANCEL_ORDER
} from './vu-http.interface';
import { ScreenSaverConfiguration } from '../../../lib/screen-saver-configuration';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { TouchTileColor } from '../../../lib/touch-tile/touch-tile-color';
import { TouchTileSwitch } from '../../../lib/touch-tile/touch-tile-switch';
import { RunningLightScenario } from '../../../lib/touch-tile/running-light-scenario';
import { CreditCardTerminalState } from '../../../lib/credit-card/credit-card-terminal-state';
import { DisplayConfiguration } from '../../../lib/display/configuration/display-configuration';

@Injectable()
export class VuHttpService extends VuHttpBaseService {
  private http: HttpClient;
  protected init() {
    this.http = this.injector.get(HttpClient);
  }

  getProducts(): Promise<Product[]> {
    return this.http
      .get('api/products')
      .toPromise()
      .then(response => this.parseProducts(response))
      .catch(this.handleError);
  }

  getProductsByIds(productIds: number[]): Promise<Product[]> {
    return this.http
      .post('api/products/products-by-ids', {
        IntArray: productIds
      })
      .toPromise()
      .then(response => this.parseProducts(response))
      .catch(this.handleError);
  }

  getProduct(productId: number): Promise<Product> {
    return this.http
      .get(`api/products/product?product_id=${productId}`)
      .toPromise()
      .then(response => Product.fromJson(response))
      .catch(this.handleError);
  }

  getSubProduct(productId: number): Promise<Product> {
    return this.http
      .get(`api/products/subproduct?product_id=${productId}`)
      .toPromise()
      .then(response => Product.fromJson(response))
      .catch(this.handleError);
  }

  getVisualItems(type: string): Promise<VisualItem[]> {
    let url = `api/${VisualItemsApiName}/?type=${type}`;
    return this.http
      .get(url)
      .toPromise()
      .then(response => this.parseVisualItems(response, type))
      .catch(this.handleError);
  }

  getDisplayConfiguration(configurationId: number): Promise<DisplayConfiguration> {
    if(!configurationId) {
      const m = `VuHttpBaseService. getDisplayConfiguration. configurationId == null`;
      this.log.error(m);
        return this.returnEmptyPromise();
    }
    let url = `api/display-items/?configuration_id=${configurationId}`;
    return this.http
      .get(url)
      .toPromise()
      .then(response => this.parseDisplayConfiguration(response))
      .catch(this.handleError);
  }

  getVuState(): Promise<VuState> {
    return this.http
      .get('api/get-vu-state')
      .toPromise()
      .then(response => {
        let vuState = VuState.fromOther(response);
        this.log.info(`VuHttpService. getVuState. ${vuState}`);
        return vuState;
      })
      .catch(this.handleError);
  }

  getVuConfiguration(): Promise<DtoVuConfiguration> {
    return this.http
      .get('api/get-vu-configuration')
      .toPromise()
      .then(response => {
        let c = DtoVuConfiguration.fromOther(response);
        this.log.info(`VuHttpService. getVuConfiguration. ${c}`);
        return c;
      })
      .catch(this.handleError);
  }

  getAcceptedCash(amount: Money): Promise<AcceptedCash> {
    this.log.info(`VuHttpService. getAcceptedCash. Begin. ${amount}`);
    return this.http
      .get(
        `api/payment/get-accepted-cash/?value=${amount.value}&currencyCode=${
        amount.currencyCode
        }`
      )
      .toPromise()
      .then(response => {
        let result = this.parseAcceptedCash(response);
        this.log.info(`VuHttpService. getAcceptedCash.  End. ${result}`);
        return result;
      })
      .catch(this.handleError);
  }

  canPayoutAmount(amount: Money): Promise<boolean> {
    this.log.info(`VuHttpService. canPayoutAmount. Begin. ${amount}`);
    return this.http
      .get(
        `api/payment/can-payout-amount/?value=${amount.value}&currencyCode=${
        amount.currencyCode
        }`
      )
      .toPromise()
      .then(response => {
        this.log.info(`VuHttpService. canPayoutAmount. True.  End. `);
        return true;
      })
      .catch(response => {
        this.log.info(`VuHttpService. canPayoutAmount. False. End. `);
        return false;
      });
  }

  getTicketUse(code: string): Promise<TicketUse[]> {
    return this.http
      .get(`api/ticket/get-ticket-use/?code=${code}`)
      .toPromise()
      .then(response => {
        let result = this.parseTicketUse(response);
        this.log.info(
          `VuHttpService. getTicketUse. ${TicketUse.toString(result)}`
        );
        return result;
      })
      .catch(this.handleError);
  }

  beginCardPaymentTransaction(amount: Money) {
    super.beginCardPaymentTransaction(amount);
    this.http
      .post(`api/payment/begin-card-transaction`, {
        Value: `${amount.value}`,
        CurrencyCode: `${amount.currencyCode}`
      })
      .toPromise()
      .catch(this.handleError);
  }

  beginPaymentTransaction(amount: Money) {
    super.beginPaymentTransaction(amount);
    this.http
      .post(`api/payment/begin-transaction`, {
        Value: `${amount.value}`,
        CurrencyCode: `${amount.currencyCode}`
      })
      .toPromise()
      .catch(this.handleError);
  }

  returnAmount(amount: Money): Promise<any> {
    super.returnAmount(amount);
    return this.http
      .post(`api/payment/return-amount`, {
        Value: `${amount.value}`,
        CurrencyCode: `${amount.currencyCode}`
      })
      .toPromise()
      .catch(this.handleError);
  }

  commitPaymentTransaction(force: boolean) {
    super.commitPaymentTransaction(force);
    this.http
      .post(`api/payment/commit-transaction`, {
        BoolValue: force
      })
      .toPromise()
      .catch(this.handleError);
  }

  appendTransactionInfo(info: string) {
    super.appendTransactionInfo(info);
    this.http
      .post(`api/payment/append-transaction-info`, { info: info })
      .toPromise()
      .catch(this.handleError);
  }

  revertPaymentTransaction() {
    super.revertPaymentTransaction();
    return this.http.post(`api/payment/revert-transaction`, {}).toPromise();
  }

  rfidCardLedEnable(value: boolean): void {
    super.rfidCardLedEnable(value);
    this.http
      .post(`api/touch-tile/rfid-card-led-enable`, { enabled: value })
      .toPromise()
      .catch(this.handleError);
  }

  revertCardTerminalTransaction(): Promise<any> {
    super.revertCardTerminalTransaction();
    return this.http.post(`api/payment/revert-card-terminal-transaction`, {}).toPromise();
  }

  barcodeLedEnable(value: boolean): void {
    super.barcodeLedEnable(value);
    this.http
      .post(`api/touch-tile/barcode-led-enable`, { enabled: value })
      .toPromise()
      .catch(this.handleError);
  }

  saveOrder(order: Order): Promise<any> {
    super.saveOrder(order);
    let dtoOrder = DtoOrder.fromOrder(order);
    return this.http
      .post(API_SAVE_ORDER, JSON.stringify(dtoOrder))
      .toPromise()
      .catch(this.handleError);
  }

  cancelOrder(order: Order): Promise<any> {
    super.cancelOrder(order);
    order.convertToRefundType();
    let dtoOrder = DtoOrder.fromOrder(order);
    return this.http
      .post(API_CANCEL_ORDER, JSON.stringify(dtoOrder))
      .toPromise()
      .catch(this.handleError);
  }

  callStaff() {
    super.callStaff();
    this.http
      .post(`api/call-staff`, {})
      .toPromise()
      .catch(this.handleError);
  }

  print(task: PrintTask): Promise<any> {
    super.print(task);
    return this.http
      .post(API_PRINT, JSON.stringify(task))
      .toPromise()
      .catch(this.handleError);
  }

  printByTemplateTypeUniqueName(templateTypeUniqueName: string, language: string): Promise<any> {
    super.printByTemplateTypeUniqueName(templateTypeUniqueName, language);
    return this.http
      .post(API_PRINT_TEMPLATE, { templateUniqueName: templateTypeUniqueName, language: language })
      .toPromise()
      .catch(this.handleError);
  }

  openFmcuApi(url: string, body: object = {}): Promise<any>{
    super.openFmcuApi(url, body);
    return this.http
      .post(url, body)
      .toPromise()
      .catch(this.handleError);
  }

  scanTicket(barcode: string) {
    super.scanTicket(barcode);
    this.http.post('api/ticket/scan', { code: barcode })
      .toPromise()
      .catch(this.handleError);
  }

  printTicket(barcode: string): Promise<any> {
    super.scanTicket(barcode);
    return this.http.post('api/ticket/print', { Code: barcode })
      .toPromise()
      .catch(this.handleError);
  }

  getScreenSaverConfiguration(): Promise<ScreenSaverConfiguration> {
    return this.http
      .get(`api/screensaver/settings`)
      .toPromise()
      .then(response => ScreenSaverConfiguration.fromJson(response))
      .catch(this.handleError);
  }

  openEnter(): Observable<boolean> {
    return this.http.get(`/api/turnstile/open_enter`).pipe(
      map(result => {
        return true;
      })
    );
  }

  closeEnter(): Observable<boolean> {
    return this.http.get(`/api/turnstile/close`).pipe(
      map(result => {
        return true;
      })
    );
  }

  demoPayment(): Observable<any> {
    return this.http.post(`/api/payment/demo-payment`, {}).pipe(
      map(result => {
        return true;
      })
    );
  }

  changeTouchTileColor(touchTileColor: TouchTileColor[]): Observable<boolean> {
    super.changeTouchTileColor(touchTileColor);
    return this.http
      .post('api/touch-tile/change-color', JSON.stringify(touchTileColor))
      .pipe(
        map(result => {
          return true;
        })
      );
  }

  changeTouchTileSwitch(
    touchTileSwitch: TouchTileSwitch[]
  ): Observable<boolean> {
    super.changeTouchTileSwitch(touchTileSwitch);
    return this.http
      .post('api/touch-tile/change-switch', JSON.stringify(touchTileSwitch))
      .pipe(
        map(result => {
          return true;
        })
      );
  }

  changeTouchTileManualMode(enabled: boolean): Observable<boolean> {
    super.changeTouchTileManualMode(enabled);
    return this.http
      .post('api/touch-tile/manual-mode-enable', { enabled: enabled })
      .pipe(
        map(result => {
          return true;
        })
      );
  }

  setRunningLight(
    runningLightScenario: RunningLightScenario
  ): Observable<boolean> {
    super.setRunningLight(runningLightScenario);
    return this.http
      .post('api/touch-tile/set-running-light', runningLightScenario)
      .pipe(
        map(result => {
          return true;
        })
      );
  }

  getAcceptedCreditCards(): Promise<CreditCard[]> {
    this.log.info(`VuHttpService. getAcceptedCreditCards. Begin.`);
    return this.http
      .get(`/api/credit-card-terminal/accepted-credit-cards`)
      .toPromise()
      .then(response => {
        let result = this.parseAcceptedCreditCards(response);
        this.log.info(`VuHttpService. getAcceptedCreditCards.  End. ${result}`);
        return result;
      })
      .catch(this.handleError);
  }

  getCreditCardTerminalState(): Observable<CreditCardTerminalState> {
    super.getCreditCardTerminalState();
    return this.http
      .get('api/credit-card-terminal/state')
      .map((x: any) => CreditCardTerminalState.fromJson(x))
      .catch(this.handleError);
  }
  getTicketInfo(barcode: string): Observable<Ticket> {
    super.getTicketInfo(barcode);
    return this.http.get(`api/ticket/get-ticket-info?code=${barcode}`)
      .pipe(
        map(result => {
          return Ticket.fromJson(result);
        })
      );
  }

  activateOneDayTicket(ticket_code: string): Observable<Ticket> {
    super.activateOneDayTicket(ticket_code);
    return this.http.post('api/ticket/activate-one-day', { Code: ticket_code })
      .pipe(
        map(result => {
          return Ticket.fromJson(result);
        })
      );
  }

  gateReadBarcode(barcode: string, baseUrl: string, openGateType: string, timeout: number): Observable<any> {
    super.gateReadBarcode(barcode, baseUrl, openGateType, timeout);
    return this.http.post(`api/gate/read-barcode`, {
      barcode: barcode,
      baseUrl: baseUrl,
      openGateType: openGateType,
      timeout: timeout
    });
  }

  changeServerLanguage(locale_id: string) {
    super.changeServerLanguage(locale_id);
    this.http
      .post(`/api/language/change`, { locale_id: locale_id })
      .toPromise()
      .catch(this.handleError);
  }
}
